/**
 * Code execution using the new MpRawMode infrastructure.
 *
 * Replaces the old editorUI.ts runCurrentFile/stopExecution that used
 * enterRawRepl + executeRawCommand + blockReplUntilString + waitForStringCallback.
 */
import type {
    EditorContext,
    EditorActorRef,
    SaveFileResult,
} from '@pyranger/types';
import { debug } from '@pyranger/utils';

import { MpRawMode } from './mpRawMode';
import { SerialTransaction } from './serialTransaction';
import { writeToTerminal } from './terminal';
import { handleSaveFileClick } from './editorUI';
import { setIsFileRunning, setIsExecutingCommand, getWriter } from './repl';
import { waitForMutexRelease } from './deviceMutex';

/**
 * Run the currently open file on the device.
 *
 * Flow:
 * 1. Optionally save the file first (if settings.saveBeforeRunning)
 * 2. Acquire mutex, enter raw REPL
 * 3. Submit user code via raw REPL protocol
 * 4. After receiving OK, switch to streaming mode: stdout goes to terminal
 * 5. Wait for \x04 (natural completion) or user interrupt (Ctrl-C)
 * 6. Read stderr, exit raw REPL, release mutex
 */
export const runCurrentFile = async ({
    actorRef,
    context,
    onAutoSave,
}: {
    actorRef: EditorActorRef;
    context: EditorContext;
    onAutoSave?: (saveFileResult: SaveFileResult) => void;
}): Promise<undefined> => {
    // If a file is currently running, stop it first and wait for cleanup
    if (context.ref.current.repl.isFileRunning) {
        debug.editor('Stopping current execution before running new file');
        await stopExecution({ context });
        // Wait for runCurrentFile's finally block to release the mutex
        await waitForMutexRelease(context.ref.current.device);
    } else if (context.ref.current.device.mutex.isLocked) {
        throw new Error('Cannot run file — device is busy');
    }

    const {
        textEditor: { currentPath, openFiles },
        settings: { saveBeforeRunning },
    } = context;

    if (currentPath === null) {
        throw new Error('No file is currently open for execution');
    }

    const code = openFiles.get(currentPath)?.content ?? null;
    if (code === null) {
        throw new Error('The file at the current path has null content');
    }

    // Don't execute empty files — nothing to run
    if (code.trim().length === 0) {
        context.showCustomAlert({
            title: 'Nothing to run',
            message: 'The file is empty.',
            variant: 'warning',
        });
        return undefined;
    }

    // Save before running if enabled
    if (saveBeforeRunning) {
        setIsExecutingCommand({ actorRef, context, isExecutingCommand: true });
        try {
            const saveFileResult = await handleSaveFileClick({
                actorRef,
                autoSave: true,
                context,
                skipExecutingCheck: true,
            });
            if (onAutoSave) {
                onAutoSave(saveFileResult);
            }
        } finally {
            setIsExecutingCommand({
                actorRef,
                context,
                isExecutingCommand: false,
            });
        }
    }

    // Now run the code
    const raw = await MpRawMode.begin(context);

    setIsFileRunning({ actorRef, context, isFileRunning: true });

    try {
        // Use execStreaming to send code and stream output to terminal
        const { stderr } = await raw.execStreaming(code, (chunk) => {
            writeToTerminal({ context, message: chunk });
        });

        // Write stderr to terminal (includes KeyboardInterrupt tracebacks)
        if (stderr.length > 0) {
            writeToTerminal({ context, message: stderr + '\n' });
        }
    } catch (err) {
        // MpRawMode errors (disconnect, timeout) — show in terminal
        const message =
            err instanceof Error ? err.message : 'Unknown execution error';
        debug.editor(`Code execution error: ${message}`);
        // Don't show cancellation errors in terminal — these happen when
        // resetREPL or another operation intentionally aborts execution
        if (!message.includes('cancelled')) {
            writeToTerminal({
                context,
                message: `\r\n${message}\r\n`,
            });
        }
    } finally {
        setIsFileRunning({ actorRef, context, isFileRunning: false });

        // Exit raw REPL while still holding the mutex to prevent races.
        // Send Ctrl-B first, then abort the raw session which releases
        // the transaction and mutex — boot banner flows to the terminal.
        try {
            const port = context.ref.current.connection.port;
            if (port) {
                const writer = getWriter({ context, port });
                await writer.write(new TextEncoder().encode('\x02')); // Ctrl-B: exit raw REPL
            }
        } catch {
            // Port may already be closed — abort will still clean up
        } finally {
            raw.abort();
        }
    }

    return undefined;
};

/**
 * Stop a running script by sending Ctrl-C through the serial port.
 *
 * This works because even though a transaction is active during execution,
 * we can still write to the port directly. The Ctrl-C causes a KeyboardInterrupt
 * on the device which terminates the running script and produces output on stderr.
 * The execStreaming() call in runCurrentFile will then complete naturally
 * (it reads until \x04 which comes after the interrupt).
 */
export const stopExecution = async ({
    context,
}: {
    context: EditorContext;
}): Promise<void> => {
    const port = context.ref.current.connection.port;
    if (!port) {
        throw new Error('No port available for stopping execution');
    }

    debug.editor('Sending Ctrl-C to stop execution');

    // Write Ctrl-C directly to the port
    const writer = getWriter({ context, port });
    const encoder = new TextEncoder();
    await writer.write(encoder.encode('\x03'));
};

/**
 * Reset the REPL to a known good state.
 * Interrupts any running program, exits raw mode, and waits for the prompt.
 * The traceback and boot banner are forwarded to the terminal so the user
 * sees the full stop sequence.
 */
export const resetREPL = async ({
    context,
}: {
    context: EditorContext;
}): Promise<void> => {
    const port = context.ref.current.connection.port;
    if (!port) {
        throw new Error('No port available for resetting the REPL');
    }

    // If a file is currently running, just stop it and let runCurrentFile's
    // finally block handle the clean exit from raw REPL (Ctrl-B → >>> prompt).
    // No need to force-cancel or do additional reset work.
    if (context.ref.current.repl.isFileRunning) {
        debug.editor('resetREPL: file is running, calling stopExecution');
        await stopExecution({ context });
        // Wait for runCurrentFile's finally block to complete cleanup
        await waitForMutexRelease(context.ref.current.device, {
            forceReleaseOnTimeout: false,
        });
        return;
    }

    // If mutex is locked but no file is running (some other operation), forcefully take over
    if (context.ref.current.device.activeTransaction) {
        context.ref.current.device.activeTransaction.cancel();
        context.ref.current.device.activeTransaction = null;
    }
    if (context.ref.current.device.mutex.isLocked) {
        context.ref.current.device.mutex.forceRelease();
        await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Acquire mutex for the reset operation
    const release = await context.ref.current.device.mutex.acquire();
    const transaction = new SerialTransaction();
    context.ref.current.device.activeTransaction = transaction;

    try {
        const writer = getWriter({ context, port });
        const encoder = new TextEncoder();

        // Ctrl-C twice to interrupt any running program
        await writer.write(encoder.encode('\x03\x03'));

        // Small delay for device to process
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Ctrl-B to exit raw mode (if in it)
        await writer.write(encoder.encode('\x02'));

        // Wait for the prompt
        try {
            await transaction.readUntil('>>> ', 3000);
        } catch {
            debug.editor('resetREPL: no prompt received, continuing');
        }
    } finally {
        // Forward captured output to terminal (traceback, boot banner, etc.)
        // Filter out raw REPL protocol noise (the single `>` character from raw mode)
        const captured = transaction.drain();
        context.ref.current.device.activeTransaction = null;
        release();

        if (captured) {
            // Remove raw REPL artifacts: standalone `>` or `OK` that aren't part of real output
            const cleaned = captured
                .replace(/^>+/gm, '')
                .replace(/^OK\r?\n/gm, '');
            if (cleaned.trim()) {
                writeToTerminal({ context, message: cleaned });
            }
        }
    }
};

/**
 * Perform a soft reset (Ctrl-D) on the device.
 * This reboots the MicroPython/CircuitPython firmware without a full hardware reset.
 * The boot banner is forwarded to the terminal.
 */
export const softReset = async ({
    context,
}: {
    context: EditorContext;
}): Promise<void> => {
    const port = context.ref.current.connection.port;
    if (!port) {
        throw new Error('No port available for soft reset');
    }

    const { resetTerminal } = await import('./terminal');

    // Clear the terminal before the reboot so we get a clean slate with just the new banner
    resetTerminal({ context });

    // Acquire mutex
    const release = await context.ref.current.device.mutex.acquire();
    const transaction = new SerialTransaction();
    context.ref.current.device.activeTransaction = transaction;

    try {
        const writer = getWriter({ context, port });
        const encoder = new TextEncoder();

        // Ctrl-C twice to interrupt
        await writer.write(encoder.encode('\x03\x03'));
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Ctrl-D for soft reboot
        await writer.write(encoder.encode('\x04'));

        // Wait for the boot banner and prompt
        try {
            await transaction.readUntil('>>> ', 5000);
        } catch {
            debug.editor('softReset: no prompt after reboot, continuing');
        }
    } finally {
        // Forward the boot banner to the terminal
        const captured = transaction.drain();
        context.ref.current.device.activeTransaction = null;
        release();

        if (captured) {
            writeToTerminal({ context, message: captured });
        }
    }
};
